import type { UseQueryResult } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, History } from "lucide-react";
import type { AuditResponse } from "../../shared/audit-schemas";
import { Button } from "../components/ui/button";
import { ContentLoading } from "../components/ContentLoading";
import { AuditEventRow, type RecordAuditEvent } from "./AuditEventRow";

export function AuditResults({
  audit,
  showCustomer,
  onRecordHistory,
}: {
  audit: UseQueryResult<AuditResponse>;
  showCustomer: boolean;
  onRecordHistory?: (event: RecordAuditEvent) => void;
}) {
  if (audit.error)
    return (
      <div role="alert" className="p-6">
        <h2 className="m-0 text-base font-semibold">Couldn’t load activity</h2>
        <p className="mt-2 text-sm text-muted-foreground">{audit.error.message}</p>
        <Button
          variant="outline"
          onClick={() => {
            void audit.refetch();
          }}
        >
          Try again
        </Button>
      </div>
    );
  if (audit.isPending) return <ContentLoading label="Loading activity…" className="p-6" />;
  if (audit.data.events.length === 0)
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <History aria-hidden="true" className="size-6 text-muted-foreground" />
        <h2 className="m-0 text-base font-semibold">No activity in this view</h2>
        <p className="m-0 max-w-sm text-sm text-muted-foreground">
          Try a wider date range or clear your filters. New activity appears after a short delay.
        </p>
      </div>
    );
  return audit.data.events.map((event) => (
    <AuditEventRow
      key={event.id}
      event={event}
      showCustomer={showCustomer}
      onRecordHistory={onRecordHistory}
    />
  ));
}

export function AuditPagination({
  audit,
  cursors,
  onChange,
}: {
  audit: UseQueryResult<AuditResponse>;
  cursors: (string | undefined)[];
  onChange: (value: (string | undefined)[]) => void;
}) {
  if (!audit.data || audit.error || (audit.data.events.length === 0 && cursors.length === 1))
    return null;
  const next = audit.data.nextCursor;
  return (
    <nav aria-label="Audit pagination" className="mt-4 flex items-center justify-between gap-3">
      <p className="m-0 text-xs text-muted-foreground tabular-nums">
        Page {cursors.length} · {audit.data.events.length} events
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={cursors.length === 1 || audit.isFetching}
          onClick={() => {
            onChange(cursors.slice(0, -1));
          }}
        >
          <ChevronLeft />
          Previous
        </Button>
        <Button
          variant="outline"
          disabled={!next || audit.isFetching}
          onClick={
            next
              ? () => {
                  onChange([...cursors, next]);
                }
              : undefined
          }
        >
          Next
          <ChevronRight />
        </Button>
      </div>
    </nav>
  );
}

export function AuditCheckedAt({ audit }: { audit: UseQueryResult<AuditResponse> }) {
  return (
    <div className="mb-3 flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
      <p className="m-0">Recent activity may take a minute or two to appear.</p>
      {audit.data && !audit.error ? (
        <p className="m-0">
          Checked{" "}
          {new Date(audit.data.checkedAt * 1000).toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
          })}
        </p>
      ) : null}
    </div>
  );
}
